import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox } from 'react-bootstrap';
import { translate } from 'react-i18next';
import { isEmpty } from 'lodash/lang';
import { trim } from 'lodash/string';
import IntlTelInput from 'react-intl-tel-input';
import { FinnishSSN } from 'finnish-ssn';
import Select from 'react-select';

import ErrorMessage from '../../components/Messages/ErrorMessage';
import i18n from '../../i18n';
import InnerHtmlContent from '../../components/InnerHtmlContent';
import { cyrillicLetterScanner } from '../../util/helpers';
import api from '../../api';

// Custom styles used by react-select 
const customStyles = {
  control: base => ({
    ...base,
    height: 42,
    minHeight: 42,
    marginTop: '10px',
    borderRadius: 0,
    paddingLeft: '6px'
  }),
  menu: base => ({
    ...base,
    zIndex: 2
  })
};

class RegistrationForm extends Component {

  constructor(props) {
    super(props);

    this.timeoutPhoneInputChange = null;

    const sotuIsValid = FinnishSSN.validate(this.props.personId);
    const withoutSotu = (props.relaxedSotuVerificationEnabled && !sotuIsValid);

    this.state = {
      allFieldsValid: false,
      address: '',
      postcode: '',
      city: '',
      phone: '',
      email: '',
      municipality: '',
      municipalityCode: '',
      acceptedTerms: false,
      language: i18n.language,
      addressValid: false,
      postcodeValid: false,
      cityValid: false,
      phoneValid: false,
      emailValid: false,
      acceptedTermsValid: false,
      municipalityValid: false,
      validationError: '',
      cyrillicLettersDetected: false,
      touched: {},
      exclusiveData: {},
      exclusiveDataValid: [],
      sotuValid: sotuIsValid,
      municipalities: [],
      registrationExclusiveFieldsFiltered: props.registrationExclusiveFields.filter(field => field.registrationCase === 'ALWAYS' ||
                                                                              (withoutSotu && field.registrationCase === 'WITHOUT_SOTU') ||
                                                                              (!withoutSotu && field.registrationCase === 'WITH_SOTU'))
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
    this.handlePhoneInputChange = this.handlePhoneInputChange.bind(this);
    this.handleExclusiveFieldChange = this.handleExclusiveFieldChange.bind(this);
  }


  componentDidMount() {
    this.getMunicipalities();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.lng !== i18n.language) {
      this.getMunicipalities();
    }
  }

  getMunicipalities() {
    api.getMunicipalities(i18n.language).then((municipalities) => {
      const municipalityList = [];
      municipalities.map(({ municipalityCode, municipalityName }) => municipalityList.push({ label: municipalityName, value: municipalityCode }));
      this.setState({ municipalities: municipalityList });
    });
  }

  getFieldClass(fieldName, isValid) {
    if (isValid) {
      return 'is-valid';
    }
    return this.isFieldInvalid(fieldName, isValid) ? 'is-invalid' : '';
  }

  validateFields() {
    const POSTAL_CODE_REGEX = /^\d{5}$/;
    const sotuValid = FinnishSSN.validate(this.props.personId);
    const { address, postcode, city, phone, acceptedTerms, email, municipality } = this.state;
    const addressValid = (address.length > 0) && !cyrillicLetterScanner(address);
    const postcodeValid = POSTAL_CODE_REGEX.test(postcode);
    const cityValid = (city.length > 0) && !cyrillicLetterScanner(city);
    const emailValid = (sotuValid && !cyrillicLetterScanner(email) && (email.length === 0 || this.isEmailValid(email))) || (!sotuValid && this.isEmailValid(email));
    const acceptedTermsValid = acceptedTerms === true;
    const phoneValid = this.isPhoneValid(phone);
    const cyrillicLettersDetected = cyrillicLetterScanner(address) || cyrillicLetterScanner(city) || cyrillicLetterScanner(email);
    const municipalityValid = (municipality.length > 0);

    const allFieldsValid = !cyrillicLettersDetected && (addressValid && postcodeValid && cityValid && phoneValid && emailValid && acceptedTermsValid && municipalityValid);
    this.setState({
      addressValid,
      postcodeValid,
      cityValid,
      phoneValid,
      emailValid,
      acceptedTermsValid,
      allFieldsValid,
      validationError: '',
      cyrillicLettersDetected,
      municipalityValid
    });
  }

  isPhoneValid(phone) {
    const telUtils = window.intlTelInputUtils;
    return telUtils.isValidNumber(phone) &&
            (telUtils.getNumberType(phone) === telUtils.numberType.MOBILE ||
              telUtils.getNumberType(phone) === telUtils.numberType.FIXED_LINE_OR_MOBILE);
  }

  isEmailValid(email) {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  }

  handlePhoneInputChange(status, inputVal, countryData, number) {
    const phone = number.replace(/\s/g, '');
    this.setState({ phone }, this.validateFields);
  }

  handleChange(event) {
    const { id: fieldName, value } = event.target;
    this.setState({ [fieldName]: trim(value) }, this.validateFields);
  }

  handleBlur(event) {
    const { id: fieldName } = event.target;
    this.markFieldAsTouched(fieldName);
  }

  markFieldAsTouched(fieldName) {
    this.setState(prevState => ({ touched: { ...prevState.touched, [fieldName]: true } }));
  }

  isFieldInvalid(fieldName, isValid) {
    return !isValid && Boolean(this.state.touched[fieldName]);
  }

  municipalitySelectStyles() {
    if (!this.isFieldInvalid('municipality', this.state.municipalityValid)) {
      return customStyles;
    }
    return {
      ...customStyles,
      control: base => ({
        ...customStyles.control(base),
        borderColor: '#d0021b',
        '&:hover': { borderColor: '#d0021b' }
      })
    };
  }

  handleMunicipalityChange(e) {
    this.setState({
      municipalityCode: trim(e.value),
      municipality: trim(e.label),
      touched: { ...this.state.touched, municipality: true }
    }, this.validateFields);
  }

  handleExclusiveFieldChange(event, index, validationPattern) {
    const { id: fieldName, value } = event.target;

    let valid = true;
    if (validationPattern) {
      valid = (value.match(validationPattern) != null);
    }
    this.state.exclusiveDataValid[index] = valid;

    const newExclusiveData = { ...this.state.exclusiveData, [fieldName]: value };
    this.setState({ exclusiveData: newExclusiveData });
  }

  handleCheckboxChange(event) {
    this.setState({
      acceptedTerms: event.target.checked,
      touched: { ...this.state.touched, acceptedTerms: true }
    }, this.validateFields);
  }

  handleSubmit(event) {
    event.preventDefault();

    // Pressing Continue counts as touching every field, so any that are still missing turn red
    // even if the user never typed anything.
    this.setState({ touched: { address: true, postcode: true, city: true, email: true, phone: true, municipality: true, acceptedTerms: true } });

    const allExclusiveFieldsValid = this.state.exclusiveDataValid.every(isValid => isValid === true);

    if (this.state.allFieldsValid && allExclusiveFieldsValid) {
      const { address, postcode, city, phone, acceptedTerms, language, email, municipality, exclusiveData } = this.state;
      const { firstName, lastName, personId } = this.props;

      if (this.props.creationIncomplete) {
        this.props.updateUser({ address, postcode, city, phone, acceptedTerms, email, municipality },
          this.props.afterSuccess);
      } else {
        this.props.registerUser({ personId, firstName, lastName, address, postcode, city, phone, acceptedTerms, language, email, municipality, exclusiveData },
          this.props.afterSuccess);
      }
    } else if (this.state.cyrillicLettersDetected) {
      this.setState({ validationError: 'cyrillicLettersNotAllowed' });
    } else {
      const fieldChecks = [
        { valid: this.state.addressValid, message: 'addressMissing' },
        { valid: this.state.postcodeValid, message: 'postcodeMissing' },
        { valid: this.state.cityValid, message: 'cityMissing' },
        { valid: this.state.phoneValid, message: 'invalidPhone' },
        { valid: this.state.emailValid, message: 'invalidEmail' },
        { valid: this.state.acceptedTermsValid, message: 'invalidAcceptedTerms' },
        { valid: this.state.municipalityValid, message: 'invalidMunicipality' },
        { valid: allExclusiveFieldsValid, message: 'validation.pattern' }
      ];
      const missing = fieldChecks.filter(check => !check.valid);
      this.setState({ validationError: missing.length === 1 ? missing[0].message : 'fillAllFields' });
    }
  }

  render() {
    if (this.props.userIdentified && !this.props.creationIncomplete) {
      return null;
    }

    const { registrationFailed, registrationFailedMessage,
            termsOfServiceUrl, t } = this.props;

    const registrationExclusiveFieldsItems = this.state.registrationExclusiveFieldsFiltered.map((field, index) =>
      <div className="form-input-group" key={field.ehrProfileProperty}>
        <input
          id={field.ehrProfileProperty}
          className={this.state.exclusiveDataValid[index] ? 'is-valid' : ''}
          type="text"
          placeholder={t(`identification:registrationForm.exclusiveFields.${field.localizationKey}`)}
          onChange={e => this.handleExclusiveFieldChange(e, index, field.validation)}
        />
      </div>
    );

    return (
      <form className="registration-form" onSubmit={e => this.handleSubmit(e)}>
        <div className="form-input-group">
          <input
            id="address"
            className={this.getFieldClass('address', this.state.addressValid)}
            type="text"
            placeholder={t('identification:registrationForm.addressPlaceholder')}
            onChange={this.handleChange}
            onBlur={this.handleBlur}
          />
        </div>
        <div className="form-input-group">
          <input
            id="postcode"
            className={this.getFieldClass('postcode', this.state.postcodeValid)}
            type="text"
            placeholder={t('identification:registrationForm.postcodePlaceholder')}
            onChange={this.handleChange}
            onBlur={this.handleBlur}
          />
        </div>
        <div className="form-input-group">
          <input
            id="city"
            className={this.getFieldClass('city', this.state.cityValid)}
            type="text"
            placeholder={t('identification:registrationForm.cityPlaceholder')}
            onChange={this.handleChange}
            onBlur={this.handleBlur}
          />
        </div>
        <Select
          id="municipality"
          className={this.state.municipalityValid ? 'municipality is-valid' : 'municipality'}
          options={this.state.municipalities}
          value={this.state.municipalities.filter(({ value }) => value === this.state.municipalityCode)}
          onChange={e => this.handleMunicipalityChange(e)}
          onBlur={() => this.markFieldAsTouched('municipality')}
          placeholder={t('identification:registrationForm.municipalityPlaceholder')}
          styles={this.municipalitySelectStyles()}
        />
        <div className="form-input-group form-input-group-phone">
          <IntlTelInput
            fieldId={'visiblePhone'}
            numberType={'MOBILE'}
            preferredCountries={['fi', 'se', 'ee']}
            style={{ width: '100%' }}
            onPhoneNumberChange={this.handlePhoneInputChange}
            onPhoneNumberBlur={() => this.markFieldAsTouched('phone')}
            useMobileFullscreenDropdown={false}
            placeholder={t('identification:registrationForm.phoneNumberPlaceholder')}
            inputClassName={`form-control ${this.state.isPhoneValid ? 'is-valid' : ''} ${this.isFieldInvalid('phone', this.state.phoneValid) ? 'is-invalid-outline' : ''}`}
          />
        </div>
        <div className="form-input-group">
          <input
            id="email"
            className={this.getFieldClass('email', this.state.emailValid)}
            type="text"
            placeholder={t('identification:registrationForm.emailPlaceholder')}
            onChange={this.handleChange}
            onBlur={this.handleBlur}
          />
        </div>
        {registrationExclusiveFieldsItems}
        <div>
          <Checkbox
            id="acceptedTerms"
            className={this.getFieldClass('acceptedTerms', this.state.acceptedTermsValid)}
            onChange={this.handleCheckboxChange}
          >
            <div className="terms-label-container">
              <div className="terms-label"><InnerHtmlContent content={t('identification:registrationForm.termsOfService')} /></div>
              <div className="terms-link"><a href={termsOfServiceUrl} target={'_blank'}><InnerHtmlContent content={t('identification:registrationForm.termsOfServiceLink')} /></a></div>
            </div>
          </Checkbox>
        </div>
        <ErrorMessage isVisible={!isEmpty(this.state.validationError) || this.state.cyrillicLettersDetected}>
          <strong>{this.state.cyrillicLettersDetected ? t('cyrillicLettersNotAllowed') : t(this.state.validationError)}</strong>
        </ErrorMessage>
        <ErrorMessage isVisible={registrationFailed}>
          <strong>{t('registrationFailedTitle')}</strong>
          { registrationFailedMessage &&
            <p>{t(registrationFailedMessage)}</p>
          }
        </ErrorMessage>
        <Button
          className="action-btn center-block login-button"
          type="submit"
        >
          {t('identification:registrationForm.continueButtonText')}
        </Button>
      </form>
    );
  }
}

RegistrationForm.propTypes = {
  registerUser: PropTypes.func.isRequired,
  userIdentified: PropTypes.bool.isRequired,
  afterSuccess: PropTypes.func,
  registrationFailed: PropTypes.bool.isRequired,
  registrationFailedMessage: PropTypes.string,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  personId: PropTypes.string.isRequired,
  termsOfServiceUrl: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  registrationExclusiveFields: PropTypes.array,
  relaxedSotuVerificationEnabled: PropTypes.bool.isRequired,
  creationIncomplete: PropTypes.bool.isRequired,
  updateUser: PropTypes.func.isRequired
};

RegistrationForm.defaultProps = {
  afterSuccess: null,
  registrationFailedMessage: null,
  firstName: '',
  lastName: '',
  personId: '',
  termsOfServiceUrl: '',
  registrationExclusiveFields: []
};

export default translate(['common', 'identification'], { wait: true })(RegistrationForm);



// WEBPACK FOOTER //
// ./src/containers/RegistrationForm/RegistrationForm.jsx